import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  CssBaseline,
} from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { getAuth, signInAnonymously } from "firebase/auth";

import QuickCollectFormSection from "../../components/QuickCollectFormSection/QuickCollectFormSection";
import {
  uploadQuickTaleImageToStorage,
  uploadQuickTaleAudioToStorage,
} from "../../../OrganizationQuickTales/hooks/QuickTalesHooks";
import { CenteredToast } from "../../../../common/alert";
import { functions } from "../../../../firebase";
import { ensureFirebaseAuth } from "../../../../utils/ensureFirebaseAuth";
import { buildQCGreetingHtml } from "../../../OrganizationQCTemplates/hooks/qcGreeting";
import { EMAIL_PATTERN } from "../../utils/validation";

/**
 * QuickCollectContainer
 * Public, unauthenticated guest submission page reached at
 *   /{organizations.handle}/{normalized area.name}/submit
 *
 * Unlike AddToQuickTale there is no pre-existing QuickTale — the guest fills in
 * a blank form and the server creates the `quicktales` doc and links it to the
 * Area. Approval state is decided server-side from `area.autoApprove`.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */

// EMAIL_PATTERN is shared with QuickCollectFormSection's inline validation so
// the two checks can never disagree. See ../../utils/validation.

/**
 * Safely load audio metadata and return duration in whole seconds.
 * Returns null if duration can't be determined.
 */
const getAudioDurationSeconds = (url, { timeoutMs = 15000 } = {}) =>
  new Promise((resolve) => {
    try {
      const audio = new Audio(url);
      audio.preload = "metadata";
      audio.crossOrigin = "anonymous";

      const cleanup = () => {
        audio.onloadedmetadata = null;
        audio.onerror = null;
      };

      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);

      audio.onloadedmetadata = () => {
        clearTimeout(timer);
        const duration = Math.floor(audio.duration);
        cleanup();
        resolve(Number.isFinite(duration) && duration > 0 ? duration : null);
      };

      audio.onerror = () => {
        clearTimeout(timer);
        cleanup();
        resolve(null);
      };
    } catch (e) {
      resolve(null);
    }
  });

const QuickCollectContainer = () => {
  const { orgHandle, areaSlug } = useParams();

  const [formConfig, setFormConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submissionStatus, setSubmissionStatus] = useState("idle");

  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
    email: "",
    firstName: "",
    lastName: "",
    audioUrl: "",
    originalAudioName: "",
    audioLength: 0,
  });

  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const isSaving = useRef(false);

  // Load org/area branding for this public link.
  useEffect(() => {
    const fetchConfig = async () => {
      if (!orgHandle || !areaSlug) {
        setError("Invalid link.");
        setLoading(false);
        return;
      }

      try {
        const auth = getAuth();
        await signInAnonymously(auth);

        const getQuickCollectForm = httpsCallable(
          functions,
          "getQuickCollectForm"
        );
        const result = await getQuickCollectForm({ orgHandle, areaSlug });
        setFormConfig(result.data);
      } catch (err) {
        console.error("Failed to load QuickCollect form:", err);
        setError(err.message || "This link is not available.");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [orgHandle, areaSlug]);

  // Upload the audio as soon as it is recorded/selected, mirroring AddToQuickTale.
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadingAudio(true);
    try {
      await ensureFirebaseAuth();

      const audioUrl = await uploadQuickTaleAudioToStorage(file.raw);

      const durationFromCaller =
        typeof file?.durationSeconds === "number" &&
        Number.isFinite(file.durationSeconds)
          ? Math.floor(file.durationSeconds)
          : null;

      const duration =
        durationFromCaller ?? (await getAudioDurationSeconds(audioUrl));

      setFormValues((prev) => ({
        ...prev,
        audioUrl,
        originalAudioName: file.raw.name,
        ...(duration !== null ? { audioLength: duration } : {}),
      }));
    } catch (err) {
      CenteredToast("error", `Error uploading audio: ${err.message}`);
    } finally {
      setUploadingAudio(false);
    }
  };

  const handleSubmit = async () => {
    if (isSaving.current) return;

    // Required: Title, Audio, Email, First Name.
    // Optional: Photo, Description, Last Name.
    const missing = [];
    if (!formValues.title?.trim()) missing.push("A Title");
    if (!formValues.audioUrl) missing.push("An Audio Recording");
    if (!formValues.email?.trim()) missing.push("An Email Address");
    if (!formValues.firstName?.trim()) missing.push("A First Name");
    if (!formValues.lastName?.trim()) missing.push("A Last Name");

    if (missing.length > 0) {
      CenteredToast("error", `Please provide: ${missing.join(", ")}.`);
      return;
    }

    if (!EMAIL_PATTERN.test(formValues.email.trim())) {
      CenteredToast("error", "Please enter a valid email address.");
      return;
    }

    isSaving.current = true;
    setSubmissionStatus("submitting");

    try {
      await ensureFirebaseAuth();

      // Upload the photo now, if one was provided (the photo is optional;
      // audio is already uploaded on selection).
      const imageUrl = uploadImage?.raw
        ? await uploadQuickTaleImageToStorage(uploadImage.raw)
        : "";

      const submitQuickCollectQuickTale = httpsCallable(
        functions,
        "submitQuickCollectQuickTale"
      );

      await submitQuickCollectQuickTale({
        orgHandle,
        areaSlug,
        title: formValues.title,
        description: formValues.description,
        email: formValues.email,
        firstName: formValues.firstName,
        lastName: formValues.lastName,
        imageUrl,
        originalPhotoName: uploadImage?.raw?.name || "",
        audioUrl: formValues.audioUrl,
        originalAudioName: formValues.originalAudioName,
        audioLength: formValues.audioLength,
      });

      setSubmissionStatus("complete");
    } catch (err) {
      console.error("QuickCollect submission failed:", err);
      CenteredToast("error", `Failed to submit: ${err.message}`);
      setSubmissionStatus("idle");
    } finally {
      isSaving.current = false;
    }
  };

  // Reset the form so the guest can submit another QuickTale without
  // reloading. Keeps formConfig (org/area/greeting) since the link is the same.
  const handleRecordAnother = () => {
    setFormValues({
      title: "",
      description: "",
      email: "",
      firstName: "",
      lastName: "",
      audioUrl: "",
      originalAudioName: "",
      audioLength: 0,
    });
    setUploadImage(null);
    setUploadAudioFile(null);
    setUploadingAudio(false);
    isSaving.current = false;
    setSubmissionStatus("idle");
    window.scrollTo(0, 0);
  };

  // Greeting comes from orgQCTemplates via getQuickCollectForm (anonymous
  // clients cannot read that collection directly). Substitute the tokens here.
  const greetingHtml = buildQCGreetingHtml({
    greeting: formConfig?.greeting,
    orgName: formConfig?.organizationName,
    areaName: formConfig?.areaName,
  });

  // Submit stays greyed out until every required field is filled in:
  // First Name, Last Name, Email, Title and Audio. Photo and Description are
  // optional. handleSubmit still re-validates, so this is UI affordance only.
  // The email must also be a valid format, so Submit never activates for an
  // address the server would reject.
  const requiredFieldsComplete =
    !!formValues.firstName?.trim() &&
    !!formValues.lastName?.trim() &&
    EMAIL_PATTERN.test(formValues.email?.trim() || "") &&
    !!formValues.title?.trim() &&
    !!formValues.audioUrl;

  if (loading) {
    return (
      <CircularProgress sx={{ position: "absolute", top: "50%", left: "50%" }} />
    );
  }

  if (error || !formConfig) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography sx={{ fontSize: 18, mt: 3 }}>
          This submission link is not available.
        </Typography>
        <Typography sx={{ fontSize: 18, mt: 3 }}>
          For help, email{" "}
          <a href="mailto:support@quicktales.com" style={{ color: "#00A7CD" }}>
            support@quicktales.com
          </a>{" "}
          or call{" "}
          <a href="tel:+14242741055" style={{ color: "#00A7CD" }}>
            424-274-1055
          </a>
          .
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: "0px 20px",
      }}
    >
      <CssBaseline />

      <Box sx={{ p: 3, textAlign: "center", zIndex: 1000 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          {formConfig.orgLogoUrl ? (
            <img
              src={formConfig.orgLogoUrl}
              alt="Organization Logo"
              style={{
                height: 50,
                width: "auto",
                objectFit: "contain",
                marginRight: "12vw",
              }}
              onError={() =>
                setFormConfig((prev) => ({ ...prev, orgLogoUrl: "" }))
              }
            />
          ) : null}
          <img
            src="/logoblue-270.png"
            alt="QuickTales Logo"
            style={{ height: 50 }}
          />
        </Box>

        <Box sx={{ mt: 5 }}>
          {formConfig.organizationName ? (
            <Typography sx={{ fontWeight: 700, fontSize: 22 }}>
              {formConfig.organizationName}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: 20 }}>
            QuickTale Audio Story Submission
          </Typography>
        </Box>
      </Box>

      <Grid
        container
        justifyContent="center"
        sx={{ flexGrow: 1, bgcolor: "#fff", pt: 4, pb: 4, mt: -4 }}
      >
        <Grid item xs={12} md={8} lg={6}>
          <QuickCollectFormSection
            formValues={formValues}
            setFormValues={setFormValues}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            handleFileUpload={handleFileUpload}
            onSubmit={handleSubmit}
            submissionStatus={submissionStatus}
            areaName={formConfig.areaName}
            greetingHtml={greetingHtml}
            onRecordAnother={handleRecordAnother}
            canEdit={submissionStatus === "idle"}
            submitDisabled={
              uploadingAudio ||
              submissionStatus !== "idle" ||
              !requiredFieldsComplete
            }
            submitLabel={uploadingAudio ? "SAVING RECORDING..." : "SUBMIT"}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuickCollectContainer;
